/**
 * Data Sources Query Functions (Super Admin)
 *
 * Fetches all data sources for debugging/admin views.
 * @module fehlzeiten/services/queries/dataSourceQueries
 */

import { supabase } from "../../../../../lib/supabase";

/** Data sources response from RPC */
export interface DataSourcesResponse {
  absence_notes: DataSourceAbsenceNote[];
  daily_log: DataSourceDailyLog | null;
  attendance_summary: DataSourceAttendanceSummary | null;
}

/** Absence note from data sources RPC */
export interface DataSourceAbsenceNote {
  id: string;
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  absence_status: string;
  absence_type: string | null;
  reason: string | null;
  submitted_via: string | null;
  approval_state: string | null;
  approval_action: string | null;
  is_excused: boolean | null;
  lateness_entries: unknown | null;
  created_at: string;
  created_by: string | null;
  created_by_name: string | null;
  approved_at: string | null;
  approved_by: string | null;
  approved_by_name: string | null;
  updated_at: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
  exclude_from_zeugnis: boolean;
}

/** Daily log from data sources RPC */
export interface DataSourceDailyLog {
  id: string;
  date: string;
  presence_status: string;
  notes: string | null;
  check_in_time: string | null;
  check_in_method: string | null;
  check_in_by: string | null;
  check_in_by_name: string | null;
  check_out_time: string | null;
  check_out_method: string | null;
  check_out_by: string | null;
  check_out_by_name: string | null;
  expected_arrival_time: string | null;
  expected_checkout_time: string | null;
  absence_note_id: string | null;
  created_via: string | null;
  created_at: string;
  created_by_profile: string | null;
  created_by_name: string | null;
  updated_at: string | null;
  last_updated_by: string | null;
  last_updated_by_name: string | null;
}

/** Attendance summary from data sources RPC */
export interface DataSourceAttendanceSummary {
  total_lessons: number;
  present_count: number;
  excused_count: number;
  unexcused_count: number;
  late_count: number;
  not_recorded_count: number;
}

/**
 * Fetches all data sources for a student on a specific date.
 */
export async function fetchDataSourcesForDate(
  studentId: string,
  date: string,
): Promise<DataSourcesResponse> {
  const { data, error } = await supabase.rpc(
    "fn_get_data_sources_for_date" as never,
    {
      p_student_id: studentId,
      p_date: date,
    } as never,
  );

  if (error) {
    throw new Error(`Failed to fetch data sources: ${error.message}`);
  }

  const result = data as DataSourcesResponse | null;
  return (
    result || {
      absence_notes: [],
      daily_log: null,
      attendance_summary: null,
    }
  );
}

/**
 * Fetches user profile name by ID.
 */
export async function fetchUserName(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("user_profiles" as never)
    .select("first_name, last_name")
    .eq("id" as never, userId as never)
    .maybeSingle();

  if (error || !data) return null;
  const profile = data as { first_name?: string; last_name?: string };
  return (
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || null
  );
}

/**
 * Fetches subject and period metadata for enrichment.
 */
export async function fetchSubjectsAndPeriods(
  subjectIds: string[],
  periodIds: string[],
): Promise<{
  subjects: Map<string, { name: string; abbreviation: string }>;
  periods: Map<string, { label: string; startTime: string; endTime: string }>;
}> {
  const subjects = new Map<string, { name: string; abbreviation: string }>();
  const periods = new Map<
    string,
    { label: string; startTime: string; endTime: string }
  >();

  if (subjectIds.length > 0) {
    const { data } = await supabase
      .from("subjects" as never)
      .select("id, name, abbreviation")
      .in("id" as never, subjectIds as never);

    if (data) {
      for (const row of data as Array<{
        id: string;
        name: string;
        abbreviation: string;
      }>) {
        subjects.set(row.id, {
          name: row.name,
          abbreviation: row.abbreviation,
        });
      }
    }
  }

  if (periodIds.length > 0) {
    const { data } = await supabase
      .from("schedule_periods" as never)
      .select("id, label, start_time, end_time")
      .in("id" as never, periodIds as never);

    if (data) {
      for (const row of data as Array<{
        id: string;
        label: string;
        start_time: string;
        end_time: string;
      }>) {
        periods.set(row.id, {
          label: row.label,
          startTime: row.start_time,
          endTime: row.end_time,
        });
      }
    }
  }

  return { subjects, periods };
}
